import { useState } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import { api } from '../lib/api';

export default function ForKlinikker() {
  const [clinicName, setClinicName] = useState('');
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [treatments, setTreatments] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await api.post('/clinic-inquiry', {
        clinic_name: clinicName, contact_name: contactName,
        phone, email, treatments,
      });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Noe gikk galt. Prøv igjen.');
    } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-[#FAF8F5] border border-transparent focus:border-[#0A1F44] rounded-lg px-4 py-3 text-[#0A1F44] focus:outline-none";
  const labelCls = "block text-xs font-semibold uppercase tracking-[0.15em] text-[#0A1F44]/70 mb-1.5";

  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'Hva er en gyldig henvendelse?', acceptedAnswer: { '@type': 'Answer', text: 'En henvendelse fra en ekte pasient med verifisert telefonnummer og samtykke til kontakt.' } },
      { '@type': 'Question', name: 'Hva skjer hvis pasienten ikke svarer?', acceptedAnswer: { '@type': 'Answer', text: 'Du faktureres kun for henvendelser der pasienten er nåbar ved minst ett forsøk.' } },
      { '@type': 'Question', name: 'Kan vi velge behandlinger vi mottar leads for?', acceptedAnswer: { '@type': 'Answer', text: 'Ja, du spesifiserer hvilke behandlinger og byer du ønsker leads fra.' } },
    ],
  };

  return (
    <Layout>
      <Seo
        title="Bli partnerklinikk – Få kvalifiserte pasienter | Specialists.no"
        description="Vi sender deg ferdigkvalifiserte pasienthenvendelser. Betal kun per lead. Kun 4+ stjerner klinikker godtas."
        canonical="https://specialists.no/for-klinikker"
        jsonLd={jsonLd}
      />
      <section className="bg-[#0A1F44] text-white py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B82F6] mb-3">FOR KLINIKKER</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold leading-tight">Få flere pasienter — betal kun per henvendelse</h1>
          <p className="text-white/70 mt-5 text-lg max-w-2xl">Vi sender deg pasienter som aktivt søker din behandling. Du ringer dem opp. Ingen binding, ingen månedsavgift.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-6">
          {[
            { n: '01', t: 'Vi sender en henvendelse', d: 'Med navn, telefon, valgt behandling og by.' },
            { n: '02', t: 'Du ringer pasienten', d: 'Innen 4 timer. Vi sender deg også en PDF med alle detaljene.' },
            { n: '03', t: 'Du betaler kun per mottatt lead', d: 'Ingen binding. Ingen månedsavgift. 500–900 kr per lead.' },
          ].map((s) => (
            <div key={s.n} className="bg-white rounded-2xl p-7 border border-[#0A1F44]/10">
              <div className="font-display text-5xl font-bold text-[#3B82F6]/20">{s.n}</div>
              <h3 className="font-display text-xl font-bold text-[#0A1F44] mt-2 mb-2">{s.t}</h3>
              <p className="text-[#64748B] text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-[#0A1F44] mb-6">Priser per lead</h2>
          <div className="bg-[#FAF8F5] rounded-2xl overflow-hidden">
            {[
              ['Botox / Fillers', '500 – 700 kr'],
              ['Hårtransplantasjon', '700 – 900 kr'],
              ['Andre behandlinger', 'Avtales'],
            ].map(([t, p]) => (
              <div key={t} className="flex justify-between items-center px-6 py-5 border-b border-[#0A1F44]/5 last:border-0">
                <div className="font-semibold text-[#0A1F44]">{t}</div>
                <div className="text-[#3B82F6] font-semibold">{p}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-2xl p-6 text-[#0A1F44]">
            <strong>Intro-tilbud:</strong> De første 5 henvendelsene er kostnadsfrie.
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-[#0A1F44] mb-6 text-center">Bli partnerklinikk</h2>
          {sent ? (
            <div className="bg-white rounded-2xl p-8 border border-[#0A1F44]/10 text-center" data-testid="clinic-inquiry-success">
              <CheckCircle className="mx-auto text-[#3B82F6] mb-3" size={36} />
              <h3 className="font-display text-xl font-bold text-[#0A1F44] mb-1">Takk!</h3>
              <p className="text-[#64748B]">Vi tar kontakt innen 2 virkedager.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white rounded-2xl p-7 border border-[#0A1F44]/10 space-y-4">
              <div><label className={labelCls}>Klinikknavn *</label><input required value={clinicName} onChange={(e) => setClinicName(e.target.value)} className={inputCls} data-testid="clinic-form-name" /></div>
              <div><label className={labelCls}>Kontaktperson *</label><input required value={contactName} onChange={(e) => setContactName(e.target.value)} className={inputCls} data-testid="clinic-form-contact" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Telefon *</label><input required value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} data-testid="clinic-form-phone" /></div>
                <div><label className={labelCls}>E-post *</label><input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="clinic-form-email" /></div>
              </div>
              <div><label className={labelCls}>Behandlinger</label><input value={treatments} onChange={(e) => setTreatments(e.target.value)} placeholder="F.eks. Botox, Fillers, Hårtransplantasjon" className={inputCls} data-testid="clinic-form-treatments" /></div>
              {error && <div className="text-sm text-red-600">{error}</div>}
              <button type="submit" disabled={loading} className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white py-3.5 rounded-full font-semibold flex items-center justify-center gap-2" data-testid="clinic-form-submit">
                {loading ? <><Loader2 className="animate-spin" size={16} /> Sender…</> : 'Send forespørsel'}
              </button>
            </form>
          )}
        </div>
      </section>
    </Layout>
  );
}
